import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import { isCacheStale, recordDataSync } from './cacheService';
import { conditionalLog } from './consoleLogger';

/**
 * Local-first Specification Service for STB Workshop
 * Prioritizes IndexedDB cache with Firestore fallback to minimize reads
 */

// ===== SPECIFICATION DATA OPERATIONS =====

/**
 * Gets all specifications with local-first architecture
 * Uses IndexedDB cache first, falls back to Firestore only when needed
 * @param {boolean} forceRefresh - Force refresh from Firestore
 * @returns {Promise<Array>} Array of specification objects with usage counts
 */
export const getAllSpecs = async (forceRefresh = false) => {
  try {
    if (!forceRefresh) {
      // Try to get from IndexedDB cache first
      const cachedSpecs = await getSpecsFromCache();
      
      if (cachedSpecs && cachedSpecs.length > 0 && !isCacheStale('specs')) {
        conditionalLog(`Using cached specification data (${cachedSpecs.length} items)`);
        
        // Deserialize dates from cached data
        const deserializedSpecs = cachedSpecs.map(spec => deserializeDates(spec));
        
        // Sort manually by priority
        deserializedSpecs.sort((a, b) => (a.prioridad || 0) - (b.prioridad || 0));
        
        return deserializedSpecs;
      }
    }
    
    // Cache miss or force refresh - fetch from Firestore and update cache
    conditionalLog('No cached specification data found or force refresh, fetching from Firestore...');
    return await fetchAndCacheSpecs();
    
  } catch (error) {
    console.error('Error getting specifications:', error);
    
    // Fallback to Firestore if IndexedDB fails
    try {
      return await fetchAndCacheSpecs();
    } catch (firestoreError) {
      console.error('Error fetching specifications from Firestore:', firestoreError);
      return [];
    }
  }
};

/**
 * Fetches specifications from Firestore and updates IndexedDB cache
 * @returns {Promise<Array>} Array of specification objects with usage counts
 */
const fetchAndCacheSpecs = async () => {
  try {
    const q = query(collection(db, 'specs'), orderBy('prioridad'));
    const querySnapshot = await getDocs(q);
    let specsList = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Calculate usage counts
    specsList = await calculateSpecUsageCounts(specsList);
    
    // Update IndexedDB cache with fresh data
    await syncSpecsToCache(specsList);
    
    // Record successful data sync for cache management
    recordDataSync('specs');
    
    conditionalLog(`Fetched ${specsList.length} specifications from Firestore and updated cache`);
    return specsList;
    
  } catch (error) {
    console.error('Error fetching specifications from Firestore:', error);
    throw error;
  }
};

/**
 * Calculate usage counts for specifications by checking equipment types
 * @param {Array} specsList - Array of specification objects
 * @returns {Promise<Array>} Specifications with usage counts
 */
const calculateSpecUsageCounts = async (specsList) => {
  try {
    // Get equipment types from cache or Firestore to count spec usage
    const { getAllTypes } = await import('./typeService');
    const typesList = await getAllTypes();
    
    return specsList.map(spec => {
      let typeUsageCount = 0;
      
      typesList.forEach(type => {
        // Check new format (specsIds array)
        if (type.specsIds && Array.isArray(type.specsIds)) {
          if (type.specsIds.includes(spec.id)) {
            typeUsageCount++;
          }
        }
        
        // Check old format (especificaciones array) for backward compatibility
        if (type.especificaciones && Array.isArray(type.especificaciones)) {
          if (type.especificaciones.includes(spec.nombre)) {
            typeUsageCount++;
          }
        }
      });
      
      return {
        ...spec,
        typeUsageCount
      };
    });
  } catch (error) {
    console.error('Error calculating specification usage counts:', error);
    // Return original list if calculation fails
    return specsList.map(spec => ({ ...spec, typeUsageCount: 0 }));
  }
};

// ===== INDEXEDDB CACHE OPERATIONS =====

/**
 * Get specifications from IndexedDB cache
 * @returns {Promise<Array|null>} Cached specifications or null if not found
 */
const getSpecsFromCache = async () => {
  return new Promise((resolve) => {
    const request = indexedDB.open('STBWorkshop', 23);
    
    request.onerror = () => {
      console.error('Error opening IndexedDB for specifications cache read');
      resolve(null);
    };
    
    request.onsuccess = (event) => {
      const db = event.target.result;
      
      if (!db.objectStoreNames.contains('specifications')) {
        conditionalLog('Specifications store does not exist in IndexedDB');
        db.close();
        resolve(null);
        return;
      }
      
      const transaction = db.transaction(['specifications'], 'readonly');
      const store = transaction.objectStore('specifications');
      const getAllRequest = store.getAll();
      
      getAllRequest.onsuccess = () => {
        db.close();
        const specs = getAllRequest.result || [];
        resolve(specs);
      };
      
      getAllRequest.onerror = () => {
        console.error('Error reading specifications from IndexedDB cache');
        db.close();
        resolve(null);
      };
    };
  });
};

/**
 * Sync specifications to IndexedDB cache
 * @param {Array} specs - Array of specification objects to cache
 * @returns {Promise<boolean>} Success status
 */
const syncSpecsToCache = async (specs) => {
  return new Promise((resolve) => {
    const request = indexedDB.open('STBWorkshop', 23);
    
    request.onerror = () => {
      console.error('Error opening IndexedDB for specifications cache sync');
      resolve(false);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Create specifications store if it doesn't exist
      if (!db.objectStoreNames.contains('specifications')) {
        const specsStore = db.createObjectStore('specifications', { keyPath: 'id' });
        conditionalLog('Created specifications object store in IndexedDB');
      }
    };
    
    request.onsuccess = (event) => {
      const db = event.target.result;
      
      // Ensure the specifications store exists
      if (!db.objectStoreNames.contains('specifications')) {
        conditionalLog('Specifications store does not exist, will be created on next upgrade');
        db.close();
        resolve(false);
        return;
      }
      
      const transaction = db.transaction(['specifications'], 'readwrite');
      const store = transaction.objectStore('specifications');
      
      // Clear existing data
      const clearRequest = store.clear();
      
      clearRequest.onsuccess = () => {
        // Add all specs with serialized dates
        const serializedSpecs = specs.map(spec => serializeDates(spec));
        
        let completed = 0;
        const total = serializedSpecs.length;
        
        if (total === 0) {
          db.close();
          resolve(true);
          return;
        }
        
        serializedSpecs.forEach(spec => {
          const addRequest = store.add(spec);
          
          addRequest.onsuccess = () => {
            completed++;
            if (completed === total) {
              db.close();
              conditionalLog(`Synced ${total} specifications to IndexedDB cache`);
              resolve(true);
            }
          };
          
          addRequest.onerror = () => {
            console.error('Error adding specification to IndexedDB cache:', spec.id);
            completed++;
            if (completed === total) {
              db.close();
              resolve(false);
            }
          };
        });
      };
      
      clearRequest.onerror = () => {
        console.error('Error clearing specifications from IndexedDB cache');
        db.close();
        resolve(false);
      };
    };
  });
};

// ===== DATE SERIALIZATION UTILITIES =====

/**
 * Serialize Firestore Timestamps to ISO strings for IndexedDB storage
 * @param {Object} spec - Specification object with potential Firestore Timestamps
 * @returns {Object} Specification object with serialized dates
 */
const serializeDates = (spec) => {
  const serialized = { ...spec };
  
  // Convert Firestore Timestamps to ISO strings
  if (serialized.createdAt?.toDate) {
    serialized.createdAt = serialized.createdAt.toDate().toISOString();
  }
  if (serialized.updatedAt?.toDate) {
    serialized.updatedAt = serialized.updatedAt.toDate().toISOString();
  }
  
  return serialized;
};

/**
 * Deserialize ISO strings back to Date objects after reading from IndexedDB
 * @param {Object} spec - Specification object with serialized dates
 * @returns {Object} Specification object with Date objects
 */
const deserializeDates = (spec) => {
  const deserialized = { ...spec };
  
  // Convert ISO strings back to Date objects
  if (typeof deserialized.createdAt === 'string') {
    deserialized.createdAt = new Date(deserialized.createdAt);
  }
  if (typeof deserialized.updatedAt === 'string') {
    deserialized.updatedAt = new Date(deserialized.updatedAt);
  }
  
  return deserialized;
};

// ===== SEARCH OPERATIONS =====

/**
 * Search specifications locally from IndexedDB cache
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Filtered specification results
 */
export const searchSpecsLocal = async (searchTerm) => {
  try {
    const specs = await getAllSpecs();
    
    if (!searchTerm) return specs;
    
    const searchTermLower = searchTerm.toLowerCase();
    
    return specs.filter(spec => {
      return (
        spec.nombre?.toLowerCase().includes(searchTermLower) ||
        spec.descripcion?.toLowerCase().includes(searchTermLower) ||
        spec.unidad?.toLowerCase().includes(searchTermLower)
      );
    });
  } catch (error) {
    console.error('Error searching specifications locally:', error);
    return [];
  }
};

/**
 * Check if a specification is used in equipment types or equipment catalog
 * @param {string} specName - Specification name to check
 * @returns {Promise<boolean>} Whether specification is in use
 */
export const checkSpecInUse = async (specName) => {
  try {
    const { getAllTypes } = await import('./typeService');
    const { getAllEquipment } = await import('./equipmentService');
    
    const [typesList, equipmentList] = await Promise.all([
      getAllTypes(),
      getAllEquipment(true) // Include archived
    ]);
    
    // Check if spec is used in equipment types
    const isUsedInTypes = typesList.some(type => {
      return type.especificaciones?.includes(specName) || 
             type.specsIds?.includes(specName); // Note: This would need the spec ID, not name
    });

    if (isUsedInTypes) return true;

    // Check if spec is used in equipment catalog
    const isUsedInEquipment = equipmentList.some(equipment => {
      return equipment.especificaciones && Object.keys(equipment.especificaciones).includes(specName);
    });

    return isUsedInEquipment;
  } catch (error) {
    console.error('Error checking specification usage:', error);
    return true; // Assume spec is in use if there's an error
  }
};